import { messaging } from '../config/firebase';
import { getToken, onMessage, deleteToken } from 'firebase/messaging';
import { db } from '../config/firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const VAPID_KEY = 'BKDhwvDuBOs7dsFPbYR4mUFgQle06COPtr61HQOfFBIPneL3Xz6rCk8gUkiJRv_0MgBL0Z1J288OVPUtr9EzsM4';
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second
const TOKEN_REFRESH_INTERVAL = 1000 * 60 * 60; // 1 hour

export const checkNotificationPermission = async (): Promise<NotificationPermission> => {
  return Notification.permission;
};

// Add retry mechanism for token operations
const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxAttempts: number = MAX_RETRY_ATTEMPTS,
  delay: number = RETRY_DELAY
): Promise<T> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error as Error;
      console.warn(`Attempt ${attempt} failed:`, error);
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  }
  
  throw lastError;
};

export const initializePushNotifications = async (userId: string): Promise<string | null> => {
  try {
    // First check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      await updateNotificationSettings(userId, false);
      return null;
    }

    // Check current permission status
    const permission = Notification.permission;

    // If already denied, update settings and return
    if (permission === 'denied') {
      console.log('Notifications are blocked by browser');
      await updateNotificationSettings(userId, false);
      return null;
    }

    // Check user's notification settings
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const settingsDoc = await getDoc(settingsRef);
    
    // If settings don't exist, create them with notifications disabled
    if (!settingsDoc.exists()) {
      const defaultSettings = {
        notifications: {
          email: true,
          push: false,
          events: true,
          comments: true,
          mentions: true
        },
        privacy: {
          showLocation: true,
          showEmail: false
        }
      };
      
      try {
        await setDoc(settingsRef, defaultSettings);
        return null;
      } catch (error) {
        console.error('Error creating default settings:', error);
        return null;
      }
    }

    const settings = settingsDoc.data();
    
    // If push notifications are disabled in settings, don't proceed
    if (!settings?.notifications?.push) {
      return null;
    }

    // If permission is granted, get the token with retry mechanism
    if (permission === 'granted') {
      return await retryOperation(async () => {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (token) {
          await saveTokenToUserProfile(userId, token);
          return token;
        }
        return null;
      });
    }

    // Don't request permission here - let the settings toggle handle that
    return null;
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return null;
  }
};

const saveTokenToUserProfile = async (userId: string, token: string) => {
  return retryOperation(async () => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      fcmToken: token,
      notificationsEnabled: true,
      lastTokenUpdate: new Date().toISOString()
    });
    console.log('FCM token saved to user profile');
  });
};

// Updated token refresh handler to use periodic token refresh
export const setupTokenRefreshHandler = () => {
  const auth = getAuth();
  
  // Set up periodic token refresh
  setInterval(async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        // Delete existing token
        await deleteToken(messaging);
        // Get new token
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (token) {
          await saveTokenToUserProfile(user.uid, token);
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }
    }
  }, TOKEN_REFRESH_INTERVAL);
};

export const recoverFailedNotifications = async (userId: string): Promise<string | null> => {
  return retryOperation(async () => {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await saveTokenToUserProfile(userId, token);
      return token;
    }
    return null;
  });
};

export const updateNotificationSettings = async (userId: string, enabled: boolean) => {
  try {
    // First update the settings document
    const settingsRef = doc(db, 'users', userId, 'settings', 'preferences');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      // Create full settings document if it doesn't exist
      const defaultSettings = {
        notifications: {
          email: true,
          push: enabled,
          events: true,
          comments: true,
          mentions: true
        },
        privacy: {
          showLocation: true,
          showEmail: false
        }
      };
      await setDoc(settingsRef, defaultSettings);
    } else {
      // Update only the push notification setting
      await updateDoc(settingsRef, {
        'notifications.push': enabled
      });
    }
    
    if (!enabled) {
      // Clear FCM token if notifications are disabled
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        fcmToken: null,
        notificationsEnabled: false
      });
    } else {
      // Only request permission if enabling and not already granted
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Re-initialize if permission was granted
          await initializePushNotifications(userId);
        } else {
          // If permission was denied, update settings back to disabled
          await updateDoc(settingsRef, {
            'notifications.push': false
          });
        }
      } else if (Notification.permission === 'granted') {
        // Re-initialize if permission was already granted
        await initializePushNotifications(userId);
      }
    }
  } catch (error) {
    console.error('Error updating notification settings:', error);
    throw error;
  }
};

export const setupPushNotificationListeners = () => {
  onMessage(messaging, (payload) => {
    console.log('Push message received:', payload);
    
    if (payload.notification) {
      const { title, body, icon } = payload.notification;
      
      try {
        new Notification(title || 'New Notification', {
          body: body || '',
          icon: icon || '/logo192.png',
          badge: '/logo192.png',
          tag: payload.data?.groupId || payload.data?.notificationId || 'default',
          requireInteraction: true,
          data: {
            ...payload.data,
            timestamp: Date.now()
          }
        });
      } catch (error) {
        console.error('Error showing notification:', error);
        // Try to recover if there's a token issue
        if (payload.data?.userId) {
          recoverFailedNotifications(payload.data.userId);
        }
      }
    }
  });
}; 